import React, { useRef } from 'react';

import { faCloudArrowUp, faImage } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useIntl } from 'react-intl';

import { parkingManagementMessages } from 'src/features/parkingManagement/utils';
import {
    GeneralBlock,
    GeneralBlockBody,
    GeneralBlockHeader,
    GeneralBlockSubtitle,
    GeneralBlockTitle,
} from 'src/registry/components/blocks/GeneralBlock';

type ImageUploadProps = {
    onChange: (file: File | null) => void;
    imagePreview?: string;
};

export const ImageUpload = ({ onChange, imagePreview }: ImageUploadProps) => {
    const { formatMessage } = useIntl();
    const [previewUrl, setPreviewUrl] = React.useState<string | null>(null);

    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleClick = () => {
        fileInputRef.current?.click();
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();

        const file = e.dataTransfer.files?.[0];
        if (!file) return;

        setPreviewUrl(URL.createObjectURL(file));
        onChange(file); // pass the actual File object
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setPreviewUrl(URL.createObjectURL(file));
        onChange(file); // pass the actual File object
    };

    const displayPreview = previewUrl ?? imagePreview;

    return (
        <div className="flex flex-col gap-2 lg:flex-row lg:gap-4">
            <GeneralBlock className="flex h-36 w-full flex-row items-center justify-between lg:max-w-96">
                <GeneralBlockHeader className="flex flex-col items-start">
                    <GeneralBlockTitle>{formatMessage(parkingManagementMessages.coverPhoto)}</GeneralBlockTitle>
                    <GeneralBlockSubtitle>
                        {formatMessage(parkingManagementMessages.coverPhotoDescription)}
                    </GeneralBlockSubtitle>
                </GeneralBlockHeader>
                <GeneralBlockBody className="mt-0">
                    {displayPreview && (
                        <img
                            src={displayPreview}
                            className="h-[125px] w-[174px] max-w-xs rounded-[8px] border"
                            alt={formatMessage(parkingManagementMessages.coverPreview)}
                        />
                    )}
                    {!displayPreview && (
                        <div className="flex h-[125px] w-[174px] max-w-xs items-center justify-center rounded-[8px] border bg-gray-100">
                            <FontAwesomeIcon icon={faImage} size="2xl" className="text-gray-400" />
                        </div>
                    )}
                </GeneralBlockBody>
            </GeneralBlock>

            <GeneralBlock
                className="flex h-36 items-center justify-center lg:w-1/2"
                onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleDrop(e);
                }}
                onDragOver={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                }}
            >
                <GeneralBlockHeader>
                    <FontAwesomeIcon icon={faCloudArrowUp} size="xl" />
                </GeneralBlockHeader>
                <GeneralBlockBody>
                    <span className="text-center">
                        <span className="text-primary-700 cursor-pointer font-bold" onClick={handleClick}>
                            {formatMessage(parkingManagementMessages.clickToUpload)}{' '}
                        </span>
                        <span>{formatMessage(parkingManagementMessages.dragAndDrop)}</span>
                    </span>
                    <input
                        type="file"
                        accept=".jpg, .jpeg, .png, .gif, .svg"
                        ref={fileInputRef}
                        style={{ display: 'none' }}
                        onChange={handleFileChange}
                    />
                </GeneralBlockBody>
            </GeneralBlock>
        </div>
    );
};
